import SearchIcon from '@mui/icons-material/Search';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Collapse, Grid } from '@mui/material';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { FormikContextType, FormikHelpers, useFormik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { Address } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { ADDRESS_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { POSTALCODE_REGEX } from '@acorn-portal/shared/src/utils/constants/Regex';
import * as Yup from 'yup';
import AddressDetailView from './AddressDetailView';
import { AddressClient, SearchResult } from '@acorn-portal/shared/src/services/lookupApi';
import { capitalizeFirstLetter, getFirstInteger } from '@acorn-portal/shared/src/utils';
import { useFormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IFormState extends Address {
	houseNameOrNumber: string;
	postcodeSearch: string;
	selected: string;
}

interface IProps {
	onChange: (value: Address | undefined) => void;
	firstFieldLabel?: string;
	postcodeLabel?: string;
	globalError?: boolean;
	initialValue?: Address;
}

const AddressLookup: FC<IProps> = ({
	onChange,
	firstFieldLabel = 'House Number or Name',
	postcodeLabel = 'Postcode',
	globalError,
	initialValue,
}) => {
	const [selectedOption, setSelectedOption] = useState<string>();
	const [address, setAddress] = useState<Address | undefined>(initialValue);
	const [options, setOptions] = useState<IOptions[]>([]);
	const [manual, setManual] = useState(false);
	const [loading, setLoading] = useState(false);

	const GetFullAddress = async (addressId: string) => {
		const service = new AddressClient(undefined, API);
		if (!loading) {
			setLoading(true);
		}
		try {
			const { line1, line2, line3, city, company, postalCode } = await service.getFullAddressByAddressId(addressId);
			setOptions([]);
			setSelectedOption(undefined);
			const companyName = company ? `${company}, ` : '';
			const newAddress = {
				line1: companyName ? `${companyName}${line1!}` : line1!,
				line2,
				line3,
				city: city || '',
				postcode: postalCode || '',
				country: 'GB',
				county: '',
			};

			setAddress(newAddress);
			onChange(newAddress);
		} catch (e) {
			console.error(e);
			throw e;
		} finally {
			setLoading(false);
		}
	};

	// Helper functions to extract specific logic
	const createFormattedAddress = (values: IFormState) => ({
		line1: values.line1 ? capitalizeFirstLetter(values.line1.trim()) : '',
		line2: values.line2 ? capitalizeFirstLetter(values.line2?.trim()) : '',
		line3: values.line3 ? capitalizeFirstLetter(values.line3?.trim()) : '',
		city: values.city ? capitalizeFirstLetter(values.city.trim()) : '',
		county: values.county ? capitalizeFirstLetter(values.county?.trim()) : '',
		country: values.country ? capitalizeFirstLetter(values.country?.trim()) : '',
		postcode: values.postcode.trim().toUpperCase(),
	});

	const resetFormValues = (formikHelpers: FormikHelpers<IFormState>) => {
		formikHelpers.setFormikState((state) => ({
			...state,
			values: {
				...state.values,
				line1: '',
				line2: '',
				line3: '',
				city: '',
				postcode: '',
				selected: '',
			},
			errors: {},
		}));
	};

	const normalizePostcode = (postcode: string) => postcode.toLocaleUpperCase().replace(/\s/g, '');

	const isAddressUnchanged = (values: IFormState) => {
		if (!address || (address?.line1 === '' && values.line1 === '')) {
			return false;
		}

		const addressInteger = getFirstInteger(address?.line1);
		const valuesInteger = getFirstInteger(values?.line1) || getFirstInteger(values?.houseNameOrNumber);

		if (addressInteger !== valuesInteger) {
			return false;
		}

		const addressPostcode = normalizePostcode(address.postcode);
		const valuesPostcode = normalizePostcode(values.postcode || values.postcodeSearch);

		return addressPostcode === valuesPostcode;
	};

	const handleManualSubmit = (values: IFormState, formikHelpers: FormikHelpers<IFormState>) => {
		const newAddress = createFormattedAddress(values);
		setAddress(newAddress);
		setManual(false);
		onChange(newAddress);
		resetFormValues(formikHelpers);
	};

	const processAddressSearch = async (postcodeSearch: string, houseNameOrNumber: string) => {
		const service = new AddressClient(undefined, API);
		setLoading(true);

		try {
			const response = await service.searchAddresses(postcodeSearch, houseNameOrNumber);
			return response;
		} catch (error) {
			// Handle error appropriately
			console.error('Error fetching addresses:', error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const mapAddressesToOptions = (addresses: SearchResult[], houseNameOrNumber: string): IOptions[] => {
		const filteredOptions = addresses.filter((res) => res.searchResult.includes(houseNameOrNumber));

		return filteredOptions.length > 1
			? filteredOptions.map((option) => ({
					label: option.searchResult,
					value: option.itemId,
				}))
			: addresses.map((option) => ({
					label: option.searchResult,
					value: option.itemId,
				}));
	};

	// Main submit handler with reduced complexity
	const handleSubmit = async (values: IFormState, formikHelpers: FormikHelpers<IFormState>) => {
		if (manual) {
			handleManualSubmit(values, formikHelpers);
			return;
		}

		if (isAddressUnchanged(values)) {
			return;
		}

		const addresses = await processAddressSearch(values.postcodeSearch, values.houseNameOrNumber);
		if (addresses.length === 1) {
			return GetFullAddress(addresses[0].itemId ?? '');
		}
		const options = mapAddressesToOptions(addresses, values.houseNameOrNumber);

		setOptions(options);
	};

	const validationSchema = Yup.object().shape({
		houseNameOrNumber: Yup.mixed().test('address-lookup', ADDRESS_ERRORS.line1, (value) => {
			if (address?.line1 === '' || value !== '') return Boolean(value);
			return true;
		}),
		postcodeSearch: Yup.string()
			.matches(POSTALCODE_REGEX, ADDRESS_ERRORS.postcodeValid)
			.test('postcode-lookup', ADDRESS_ERRORS.postCodeRequired, (value) => {
				if (address?.postcode === '' || value !== '') return Boolean(value);
				return true;
			}),
		line1: Yup.string().test('manual-form', ADDRESS_ERRORS.line1, (value) => {
			if (manual) return Boolean(value);
			return true;
		}),
		city: Yup.string().test('manual-form', ADDRESS_ERRORS.city, (value) => {
			if (manual) return Boolean(value);
			return true;
		}),
		postcode: Yup.string()
			.matches(POSTALCODE_REGEX, ADDRESS_ERRORS.postcodeValid)
			.test('manual-form', ADDRESS_ERRORS.postCodeRequired, (value) => {
				if (manual) return Boolean(value);
				return true;
			}),
		selected: Yup.mixed().test('select-option', ADDRESS_ERRORS.searched, (value) => {
			if (options.length > 0) return Boolean(value);
			return true;
		}),
	});

	const formik = useFormik<IFormState>({
		initialValues: {
			houseNameOrNumber: initialValue ? getFirstInteger(initialValue.line1) : '',
			postcodeSearch: initialValue ? initialValue.postcode : '',
			line1: '',
			line2: undefined,
			line3: undefined,
			city: '',
			postcode: '',
			selected: '',
		},
		validationSchema,
		onSubmit: handleSubmit,
	});

	useFormikLogErrors(formik as FormikContextType<unknown>);

	useEffect(() => {
		if (globalError && !formik.errors.houseNameOrNumber && address?.line1 === '') {
			formik.setFormikState((state) => ({
				...state,
				errors: {
					houseNameOrNumber: ADDRESS_ERRORS.line1,
					postcodeSearch: ADDRESS_ERRORS.postCodeRequired,
				},
				touched: {
					houseNameOrNumber: true,
					postcodeSearch: true,
				},
			}));
		}
	}, [globalError, formik.errors, address]);

	const onEdit = () => {
		formik.setFormikState((state) => ({
			...state,
			values: {
				...state.values,
				...address,
			},
		}));
		setManual(true);
	};

	const onSelectOption = async (addressId: string) => {
		setSelectedOption(addressId);
		await formik.setFieldValue('selected', addressId);
		await GetFullAddress(addressId);
		formik.setErrors({});
	};

	return (
		<Box
			sx={{ mb: 3 }}
			onKeyDown={(ev) => {
				if (ev.key === 'Enter') {
					ev.preventDefault();
					formik.handleSubmit();
				}
			}}
		>
			<FormTextField
				id='houseNameOrNumber'
				name='houseNameOrNumber'
				label={firstFieldLabel}
				value={formik.values.houseNameOrNumber}
				onChange={(e) => {
					formik.handleChange(e);
					if (options.length > 0) {
						setOptions([]);
					}
				}}
				onBlur={formik.handleBlur}
				error={formik.touched.houseNameOrNumber && Boolean(formik.errors.houseNameOrNumber)}
				helperText={formik.touched.houseNameOrNumber && formik.errors.houseNameOrNumber}
			/>
			<FormTextField
				autoComplete='postal-code'
				onBlur={formik.handleBlur}
				id='postcodeSearch'
				name='postcodeSearch'
				label={postcodeLabel}
				value={formik.values.postcodeSearch}
				onChange={(e) => {
					formik.handleChange(e);
					if (options.length > 0) {
						setOptions([]);
					}
				}}
				error={formik.touched.postcodeSearch && Boolean(formik.errors.postcodeSearch)}
				helperText={formik.touched.postcodeSearch && formik.errors.postcodeSearch}
			/>
			{!manual && (
				<Box
					display='flex'
					justifyContent='flex-end'
					sx={(theme) => ({
						pr: '20%',
						mb: 3,
						[theme.breakpoints.down('md')]: {
							width: '100%',
							pr: 0,
						},
					})}
				>
					<LoadingButton
						sx={(theme) => ({
							[theme.breakpoints.down('md')]: {
								width: '100%',
							},
						})}
						variant='contained'
						color='primary'
						type='button'
						onClick={() => formik.handleSubmit()}
						endIcon={<SearchIcon />}
						loading={loading}
						aria-label='find-address-button'
						name='find-address-button'
					>
						Find Address
					</LoadingButton>
				</Box>
			)}
			<Collapse in={options.length > 0}>
				{options.length > 0 && (
					<Box
						sx={(theme) => ({
							backgroundColor: theme.palette.grey[300],
							borderRadius: 2,
							pt: 2,
							mb: 3,
							'& .MuiOutlinedInput-root': {
								backgroundColor: theme.palette.background.paper,
							},
							[theme.breakpoints.down('md')]: {
								pr: 0,
								p: 2,
							},
						})}
					>
						<SelectFormField
							id='selected'
							name='selected'
							value={selectedOption}
							label='Please select an address'
							data-testid='select-address-dropdown'
							onChange={(value) => onSelectOption(value)}
							options={options}
							error={formik.touched.selected && Boolean(formik.errors.selected)}
							helperText={formik.touched.selected && formik.errors.selected}
						/>
					</Box>
				)}
			</Collapse>
			<Collapse in={(Boolean(address) && address?.line1 !== '') || manual}>
				<Box
					sx={(theme) => ({
						backgroundColor: manual ? theme.palette.grey[300] : 'transparent',
						borderRadius: manual ? 2 : 0,
						pr: manual ? 0 : '20%',
						pl: manual ? 0 : 1,

						[theme.breakpoints.down('md')]: {
							pr: 0,
							p: manual ? 2 : 0,
							pl: manual ? 2 : 1,
						},
					})}
				>
					<Grid container direction='row' spacing={1}>
						{!manual && <Grid size={{ xs: 12, md: 6 }} />}
						<Grid size={12} sx={{ overflowWrap: 'break-word' }}>
							{manual ? (
								<Box
									sx={(theme) => ({
										borderRadius: 2,
										overflowWrap: 'break-word',
										py: 2,
										mb: 3,
										'& .MuiOutlinedInput-root': {
											backgroundColor: theme.palette.background.paper,
										},
									})}
								>
									<FormTextField
										id='line1'
										name='line1'
										label='Address line 1'
										autoComplete='address-line1'
										value={formik.values.line1}
										onChange={formik.handleChange}
										error={formik.touched.line1 && Boolean(formik.errors.line1)}
										helperText={formik.touched.line1 && formik.errors.line1}
										onBlur={formik.handleBlur}
									/>
									<FormTextField
										id='line2'
										name='line2'
										label='Address line 2'
										autoComplete='address-line2'
										value={formik.values.line2 || ''}
										onChange={formik.handleChange}
										error={formik.touched.line2 && Boolean(formik.errors.line2)}
										helperText={formik.touched.line2 && formik.errors.line2}
										onBlur={formik.handleBlur}
									/>
									<FormTextField
										id='line3'
										name='line3'
										label='Address line 3'
										autoComplete='address-line3'
										value={formik.values.line3 || ''}
										onChange={formik.handleChange}
										error={formik.touched.line3 && Boolean(formik.errors.line3)}
										helperText={formik.touched.line3 && formik.errors.line3}
										onBlur={formik.handleBlur}
									/>
									<FormTextField
										id='city'
										name='city'
										label='City'
										autoComplete='address-level2'
										value={formik.values.city}
										onChange={formik.handleChange}
										error={formik.touched.city && Boolean(formik.errors.city)}
										helperText={formik.touched.city && formik.errors.city}
										onBlur={formik.handleBlur}
									/>
									<FormTextField
										id='postcode'
										name='postcode'
										label='Postcode'
										autoComplete='postal-code'
										value={formik.values.postcode}
										onChange={formik.handleChange}
										error={formik.touched.postcode && Boolean(formik.errors.postcode)}
										helperText={formik.touched.postcode && formik.errors.postcode}
										onBlur={formik.handleBlur}
									/>
									<Box
										display='flex'
										justifyContent='flex-end'
										sx={(theme) => ({
											pr: '20%',
											[theme.breakpoints.down('md')]: {
												width: '100%',
												pr: 0,
											},
										})}
									>
										<Button
											sx={(theme) => ({
												mr: 2,
												[theme.breakpoints.down('md')]: {
													width: '50%',
												},
											})}
											variant='contained'
											color='secondary'
											onClick={() => {
												formik.setFormikState((state) => ({
													...state,
													values: {
														...state.values,
														line1: '',
														line2: '',
														line3: '',
														city: '',
														postcode: '',
													},
												}));
												setManual(false);
											}}
										>
											Cancel
										</Button>
										<Button
											sx={(theme) => ({
												[theme.breakpoints.down('md')]: {
													width: '50%',
												},
											})}
											variant='contained'
											color='primary'
											type='button'
											onClick={() => formik.handleSubmit()}
										>
											Submit
										</Button>
									</Box>
								</Box>
							) : (
								<Box
									sx={(theme) => ({
										maxWidth: '600px',
										[theme.breakpoints.up('md')]: {
											marginLeft: 'auto',
										},
									})}
								>
									<AddressDetailView onEdit={onEdit} address={address as Address} />
								</Box>
							)}
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</Box>
	);
};

export default AddressLookup;
